import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store';
import useTranslations from '../../../hooks/useTranslations';
import { FieldTypes, IFormFields } from '../../../models/form';
import Filters from '../../../components/Container/Filters/Filters';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import Title from '../../../components/UI/Title/Title';
import Alert from '../../../components/UI/Alert/Alert';
import { CustomerAssignmentsActions } from '../../../store/entities/customerAssignments/customerAssignments.actions';
import { VouchersActions } from '../../../store/entities/vouchers/vouchers.actions';
import { CustomersActions } from '../../../store/entities/customers/customers.actions';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { IPage } from '../../../models/page';
import { TableActions } from '../../../store/entities/table/table.actions';
import {
  getDateTimeAndSeconds,
  sortDataByDate,
} from '../../../utils/formatters';
import { customerAssignmentsListSearchValidation } from '../../../utils/formValidation/customerAssignmentsList';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import { CONSTANTS } from '../../../utils/constants';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import { IButton } from '../../../models/button';
import { PERMISSIONS } from '../../../utils/permissions';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import { getCustomerSite } from '../../../utils/addressFormatters';
import { SiteTypes } from '../../../models/pricingPlan';
import { getCustomerVouchers } from '../voucherHelpers';
import useVoucherDynamicOptions from '../../../hooks/useVoucherDynamicOptions';
import useSiteOptions from '../../../hooks/useSiteOptions';
import { useActiveOrderValidation } from '../../../hooks/useActiveOrderValidation';

const CustomerAssignmentsList = ({ permission, nextRoute }: IPage) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const { getValueFromQuery, params } = useUrlSearchParam();
  const { customerAssignments } = useAppSelector(
    (state) => state.customerAssignments
  );
  const { vouchers } = useAppSelector((state) => state.vouchers);
  const { siteListDetails } = useAppSelector((state) => state.sites);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (store) => store.auth
  );
  const { appLanguage } = useAppSelector((state) => state.general);
  const { statuses: voucherStatusOptions } = useVoucherDynamicOptions();
  const { nextPageNumber, currentPage, sortModel } = useAppSelector(
    (state) => state.table
  );
  const [rows, setRows] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertColor, setAlertColor] = useState<'success' | 'error'>('success');
  const { checkActiveOrders, renderActiveOrderDialog } =
    useActiveOrderValidation();

  const showAlert = (message: string, color: 'success' | 'error' = 'success') => {
    setAlertMessage(message);
    setAlertColor(color);
    setShow(true);
    setTimeout(() => setShow(false), 5000);
  };
  const siteList = useSiteOptions(SiteTypes.residential);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    if (permission) {
      if (currentPage !== 1) {
        dispatch(TableActions.setTableData({ currentPage: 1 }));
      }
      dispatch(CustomerAssignmentsActions.getCustomerAssignments(params));
      dispatch(VouchersActions.getVouchers());
    }
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        dispatch(
          CustomerAssignmentsActions.getCustomerAssignments(
            params,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        dispatch(
          CustomerAssignmentsActions.getCustomerAssignments(
            null,
            sortModel,
            false
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel]);

  const columns: any[] = useMemo(
    () => [
      {
        id: 'phoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.filters.phoneNumber'),
      },
      {
        id: 'fullNameFromContractor',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.fullNameFromContractor'),
      },
      {
        id: 'siteId',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.filters.siteId'),
      },
      {
        id: 'mailBoxNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.filters.mailBoxNumber'),
      },
      {
        id: 'expiryDate',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.expiryDate'),
      },
      {
        id: 'durationPROInMonths',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.durationPROInMonths'),
      },
      {
        id: 'proVoucherStatus',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.proVoucherStatus'),
      },
      {
        id: 'durationMailBoxInMonths',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.durationMailBoxInMonths'),
      },
      {
        id: 'mailBoxVoucherStatus',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.table.mailBoxVoucherStatus'),
      },
      {
        id: 'created',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.filters.created'),
      },
      {
        id: 'lastModified',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customerAssignments.filters.lastModified'),
      },
    ],
    [rcTranslate]
  );

  const openEdit = (phoneNumber: string) => {
    navigate(
      `/${CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.CHILDREN.CUSTOMER_ASSIGNMENT_DETAILS}/${phoneNumber}`
    );
  };

  const navigateToCustomerDetails = useCallback(
    (phoneNumber: string) => {
      navigate(
        '/' +
          CONSTANTS.ROUTES.CUSTOMERS.BASE +
          '/' +
          CONSTANTS.ROUTES.CUSTOMERS.CHILDREN.CUSTOMER_LIST +
          `/?phoneNumber=${phoneNumber}`
      );
    },
    [navigate]
  );

  const navigateToActionPermissionsAsAuthorizer = useCallback(
    (phoneNumber: string) => {
      navigate(
        `/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.CHILDREN.ACTION_PERMISSIONS_LIST}?authorizerPhoneNumber=${phoneNumber}`
      );
    },
    [navigate]
  );

  const navigateToSubCustomers = useCallback(
    (phoneNumber: string) => {
      navigate(
        `/${CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.CHILDREN.SUB_CUSTOMERS_LIST}?primaryCustomerPhoneNumber=${phoneNumber}`
      );
    },
    [navigate]
  );

  const navigateToCustomerOrders = useCallback(
    async (phoneNumber: string) => {
      const customers: any[] | undefined = await dispatch(
        CustomersActions.getCustomers({ phoneNumber }, undefined, false)
      );
      const customer = customers?.[0];
      if (!customer?.customerId) {
        showAlert('customerAssignments.actions.customerNotFound', 'error');
        return;
      }
      navigate(
        '/' +
          CONSTANTS.ROUTES.ORDERS_MANAGEMENT.BASE +
          '/' +
          CONSTANTS.ROUTES.ORDERS_MANAGEMENT.CHILDREN.ORDERS_LIST +
          `/?CustomerId=${customer.customerId}`,
        { state: { customerId: customer.customerId } }
      );
    },
    [dispatch, navigate]
  );

  const handleRemoveCustomerAssignment = async (
    phoneNumber: string,
    customerName: string
  ) => {
    // Check for active delivery orders using the hook
    const hasActiveOrders = await checkActiveOrders(
      phoneNumber,
      customerName,
      false
    );

    if (!hasActiveOrders) {
      // Proceed with removal if no active orders
      dispatch(
        CustomerAssignmentsActions.removeCustomerAssignment(phoneNumber)
      );
    }
  };

  const mapCustomersAssignmentsToTableRows = useCallback(
    (customerAssignments: any[]) => {
      return customerAssignments.map((customerAssignment, index) => {
        // Match vouchers by siteId and phoneNumber
        const { proVoucher, mailBoxVoucher } = getCustomerVouchers(
          vouchers,
          customerAssignment.siteId,
          customerAssignment.phoneNumber
        );

        const expiry = proVoucher?.expiryDate ?? mailBoxVoucher?.expiryDate;
        const customerObject: any = {
          id: index,
          phoneNumber: customerAssignment.phoneNumber,
          fullNameFromContractor: customerAssignment.fullNameFromContractor,
          siteId: getCustomerSite(
            customerAssignment.siteId,
            siteListDetails,
            appLanguage
          ),
          mailBoxNumber: customerAssignment.lockerNumber,
          expiryDate: expiry ? getDateTimeAndSeconds(expiry) : '',
          durationPROInMonths: proVoucher?.durationInMonths ?? '--',
          proVoucherStatus: proVoucher?.status
            ? (voucherStatusOptions.find((s) => s.id === proVoucher.status)
                ?.label ?? '--')
            : '--',
          durationMailBoxInMonths: mailBoxVoucher?.durationInMonths ?? '--',
          mailBoxVoucherStatus: mailBoxVoucher?.status
            ? (voucherStatusOptions.find((s) => s.id === mailBoxVoucher.status)
                ?.label ?? '--')
            : '--',
          created: customerAssignment.created
            ? getDateTimeAndSeconds(customerAssignment.created)
            : '',
          lastModified: customerAssignment.lastModified
            ? getDateTimeAndSeconds(customerAssignment.lastModified)
            : '',
          actions: (
            <ActionButtons
              menuItems={[
                {
                  label: 'customerAssignments.actions.edit',
                  onClick: () => openEdit(customerAssignment.phoneNumber),
                  show: appPermissions.includes(
                    PERMISSIONS.UPDATE_CUSTOMER_ASSIGNMENT
                  ),
                },
                {
                  label: 'customerAssignments.actions.remove',
                  onClick: () =>
                    handleRemoveCustomerAssignment(
                      customerAssignment.phoneNumber,
                      customerAssignment.fullNameFromContractor
                    ),
                  show: appPermissions.includes(
                    PERMISSIONS.REMOVE_CUSTOMER_ASSIGNMENT
                  ),
                },
                {
                  label: 'customers.customerList.actions.customerDetails',
                  onClick: () =>
                    navigateToCustomerDetails(customerAssignment.phoneNumber),
                  show: appPermissions.includes(
                    PERMISSIONS.GET_CUSTOMER_DETAILS
                  ),
                },
                {
                  label: 'customerAssignments.actions.viewActionPermissionsAsAuthorizer',
                  onClick: () =>
                    navigateToActionPermissionsAsAuthorizer(customerAssignment.phoneNumber),
                  show: appPermissions.includes(
                    PERMISSIONS.GET_ACTION_PERMISSIONS
                  ),
                },
                {
                  label: 'customerAssignments.actions.viewSubCustomers',
                  onClick: () =>
                    navigateToSubCustomers(customerAssignment.phoneNumber),
                  show: appPermissions.includes(
                    PERMISSIONS.GET_SUB_CUSTOMERS_REQUESTS
                  ),
                },
                {
                  label: 'customerAssignments.actions.viewOrders',
                  onClick: () =>
                    navigateToCustomerOrders(customerAssignment.phoneNumber),
                  show: appPermissions.includes(PERMISSIONS.GET_ORDERS),
                },
              ]}
              hide={
                [
                  PERMISSIONS.REMOVE_CUSTOMER_ASSIGNMENT,
                  PERMISSIONS.UPDATE_CUSTOMER_ASSIGNMENT,
                  PERMISSIONS.GET_CUSTOMER_DETAILS,
                  PERMISSIONS.GET_ACTION_PERMISSIONS,
                  PERMISSIONS.GET_SUB_CUSTOMERS_REQUESTS,
                  PERMISSIONS.GET_ORDERS,
                ].filter((p) => appPermissions.includes(p)).length === 0
              }
            />
          ),
        };

        return customerObject;
      });
    },
    [
      vouchers,
      appPermissions,
      rcTranslate,
      dispatch,
      appLanguage,
      siteListDetails,
      voucherStatusOptions,
      navigateToCustomerDetails,
      navigateToActionPermissionsAsAuthorizer,
      navigateToCustomerOrders,
    ]
  );

  useEffect(() => {
    if (!customerAssignments?.length) return setRows([]);
    const customersRows =
      mapCustomersAssignmentsToTableRows(customerAssignments);
    setRows(customersRows);
  }, [customerAssignments, vouchers, mapCustomersAssignmentsToTableRows]);

  const searchFields: IFormFields = {
    initialValues: {
      customerPhoneNumber: getValueFromQuery('customerPhoneNumber'),
      siteId: getValueFromQuery('siteId', 'number'),
      lockerNumber: getValueFromQuery('lockerNumber'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.TEL,
            id: 'customerPhoneNumber',
            label: 'customerAssignments.filters.phoneNumber',
            props: { inputProps: { maxLength: 14 } },
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'siteId',
            label: 'customerAssignments.table.siteId',
            items: siteList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.TEXT,
            id: 'lockerNumber',
            label: 'customerAssignments.filters.lockerNumber',
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  const tableButtons: IButton[] = [
    {
      title: 'customerAssignments.insertRange.addButton',
      onClick: () => {
        navigate(
          '/' +
            CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.CHILDREN
              .CUSTOMER_ASSIGNMENTS_INSERT_RANGE
        );
      },
      show: appPermissions.includes(PERMISSIONS.UPSERT_CUSTOMER_ASSIGNMENTS),
    },
  ];

  return (
    <div className="pageContainer">
      <Title text={'customerAssignments.pageTitle'} />

      <Filters
        fields={searchFields}
        filtersValidation={customerAssignmentsListSearchValidation}
      />

      <TableButtons generalButtons={[]} tableButtons={tableButtons} />

      <div className="customerAssignmentsList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={false}
          expandable={false}
          showId={false}
        />
      </div>

      {renderActiveOrderDialog()}
      {show && <Alert message={rcTranslate(alertMessage)} color={alertColor} />}
    </div>
  );
};

export default CustomerAssignmentsList;
